// admin-client/src/contexts/SettingsContext.jsx
// =============================================================================
// Loads the public app settings once on mount (GET /api/settings/public).
// Currently exposes the universal app timezone — used by the date helpers
// and the calendar grids so display matches the configured tz regardless of
// the browser's locale. Admins re-load via `refresh()` after PATCHing.
// =============================================================================

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { setActiveTimezone } from '../lib/datetime.js';

const SettingsContext = createContext({
  timezone: 'Asia/Hong_Kong',
  ready: false,
  refresh: () => {},
});

export function SettingsProvider({ children }) {
  const [tz, setTz] = useState('Asia/Hong_Kong');
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.settings.public();
      const next = r.settings?.timezone || 'Asia/Hong_Kong';
      setTz(next);
      setActiveTimezone(next);
    } catch {
      // Pre-login or network blip — fall back to the default.
      setActiveTimezone('Asia/Hong_Kong');
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <SettingsContext.Provider value={{ timezone: tz, ready, refresh }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
